import { ActionBar } from "$components/action-bar/action-bar";
import { useRegisterActionBar } from "$components/action-bar/action-bar-offset";
import { ActionBarSpacer } from "$components/action-bar/action-bar-spacer";
import type { ActionItem } from "$components/action-bar/action-item";
import { ErrorState } from "$components/error-state";
import { CheckIcon, CloseV2Icon, PlusIconBorderless } from "$components/icons";
import { Page } from "$components/page";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import {
  AllStockTakeLotFieldsFragment,
  mapStockTakeLotsFragmentToRows,
} from "$routes/inventory/_lib/stock-take-lots-fragment";
import StockTakeProgressBanner from "$routes/inventory/_lib/stock-take-progress-banner";
import StockTakeSummaryTable from "$routes/inventory/_lib/stock-take-summary-table";
import { useFacilityLocationName } from "$routes/inventory/_lib/use-facility-locations";
import {
  linkToInventory,
  linkToStockTakeReview,
} from "$routes/inventory/links";
import { gql } from "@apollo/client";
import { StockTakeLotsSearchOrder } from "elephant-web/gql";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { useOngoingStockTakeQuery } from "./__generated__/page.gql";
import OngoingStockTakeAddItem from "./_lib/ongoing-stock-take-add-item";
import OngoingStockTakeSearchFields from "./_lib/ongoing-stock-take-search-fields";
import useSortByOptions, {
  OngoingStockTakeSearchOrder,
  type StockTakeSortOption,
} from "./_lib/use-sort-by-options";

const PAGE_SIZE = 20;

gql`
  ${AllStockTakeLotFieldsFragment}

  query ongoingStockTake(
    $stockTakeId: ID!
    $facilityId: ID!
    $input: StockTakeLotsInput
    $options: StockTakeLotsOptions
  ) {
    stockTake(input: { stockTakeId: $stockTakeId, facilityId: $facilityId }) {
      id
      locationId
      lots(input: $input, options: $options) {
        items {
          ...AllStockTakeLotFields
        }
      }
      aggregateInfo {
        totalLotCount
        reviewedLotCount
      }
    }
  }
`;

const StockTake = (): JSX.Element => {
  const { stockTakeId } = useParams<{ stockTakeId: string }>();
  const { facilityId } = useUser();
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<StockTakeSortOption | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [showAddItem, setShowAddItem] = useState<boolean>(false);
  const sortByOptions = useSortByOptions();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const actionBarRef = useRef<HTMLElement>(null);
  useRegisterActionBar(actionBarRef);

  const { data, loading, error, refetch } = useOngoingStockTakeQuery({
    variables: {
      stockTakeId,
      facilityId,
      input: {
        name: debouncedSearchTerm,
      },
      options: {
        orderBy: orderBy?.field ?? StockTakeLotsSearchOrder.Name,
        orderDirection: orderBy?.direction,
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
      },
    },
  });

  const locationName = useFacilityLocationName(data?.stockTake?.locationId);

  if (error) {
    return <ErrorState error={error} />;
  }

  const totalCount = data?.stockTake?.aggregateInfo.totalLotCount;
  const reviewedCount = data?.stockTake?.aggregateInfo.reviewedLotCount;

  const stockTakeRows = mapStockTakeLotsFragmentToRows(
    data?.stockTake?.lots.items ?? [],
  );

  const actions: ActionItem[] = [
    {
      id: "inventory.stockTake.header.cancel",
      defaultMessage: "Cancel Take",
      icon: <CloseV2Icon />,
      onClick: () => navigate(linkToInventory()),
      testId: "stockTakeHeaderCancelTakeButton",
    },
    {
      id: "inventory.stockTake.header.addItem",
      defaultMessage: "Add Item",
      icon: <PlusIconBorderless />,
      onClick: () => setShowAddItem(true),
      testId: "stockTakeHeaderAddItemButton",
    },
    {
      id: "inventory.stockTake.header.review",
      defaultMessage: "Review",
      variant: "primary",
      icon: <CheckIcon />,
      onClick: () => navigate(linkToStockTakeReview(stockTakeId)),
    },
  ];

  return (
    <>
      <StockTakeProgressBanner
        loading={loading}
        totalCount={totalCount}
        reviewedCount={reviewedCount}
      />
      <Page data-dense>
        <OngoingStockTakeSearchFields
          locationName={locationName}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
        />
        {showAddItem && (
          <OngoingStockTakeAddItem
            stockTakeId={stockTakeId}
            facilityId={facilityId}
            onCancel={() => setShowAddItem(false)}
            onComplete={() => {
              setOrderBy(
                sortByOptions[OngoingStockTakeSearchOrder.MostRecentlyReviewed],
              );
              refetch();
              setShowAddItem(false);
              toast.success(
                formatMessage({
                  id: "inventory.stockTake.ongoing.addItem.success",
                  defaultMessage: "Item added successfully",
                }),
              );
            }}
            onError={(err) => {
              toast.error(err.message);
            }}
          />
        )}
        <StockTakeSummaryTable
          loading={loading}
          editableReviewedQuantity
          stockTakeRows={stockTakeRows}
          totalRows={totalCount}
          currentPage={currentPage}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
        />
        <ActionBarSpacer />
      </Page>
      <ActionBar actions={actions} containerRef={actionBarRef} />
    </>
  );
};

export default StockTake;
